import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { PageHeader, PageHeaderSkeleton, Stack, useDelayedLoading } from '@leanin/ui';
import {
  useApproveMembershipRequest,
  useDeclineMembershipRequest,
} from '../../../lib/mutations/groups';
import { useMessageMember } from '../../groups/use-message-member';
import { InlineStatus } from '../../InlineStatus';
import { AdminJoinRequestsCard } from './AdminJoinRequestsCard';
import { AdminJoinRequestsCardSkeleton } from './AdminJoinRequestsCard.skeleton';
import { useReviewableJoinRequests } from './use-reviewable-join-requests';

// Pending join requests, for circles and networks. (Spaces don't have
// request-to-join.) Each kind's queries differ in URL but share a shape;
// dispatched on `kind`.

type Props = {
  readonly kind: 'circle' | 'network';
  readonly groupId: string;
};

export function GroupRequestsSection({ kind, groupId }: Props) {
  const t = useTranslations('Group');
  // The reviewable set already excludes the viewer's own pending request (a
  // leader can't decide their own — #46); the sidebar badge reads its length
  // from the same hook, so count and list can never drift.
  const { requests, isLoading } = useReviewableJoinRequests(kind, groupId);

  const approve = useApproveMembershipRequest();
  const decline = useDeclineMembershipRequest();
  const { messageMember, error: messageError } = useMessageMember();
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const isPending = approve.isPending || decline.isPending;

  const handleApprove = (requestId: string) => {
    setDecisionError(null);
    approve.mutate(
      { groupId, requestId, kind },
      {
        onError: (err) =>
          setDecisionError(
            err instanceof Error
              ? err.message
              : t('admin.requests.approveError'),
          ),
      },
    );
  };
  const handleDecline = (requestId: string) => {
    setDecisionError(null);
    decline.mutate(
      { groupId, requestId, kind },
      {
        onError: (err) =>
          setDecisionError(
            err instanceof Error
              ? err.message
              : t('admin.requests.declineError'),
          ),
      },
    );
  };

  const showSkeleton = useDelayedLoading(isLoading);
  if (showSkeleton) {
    return (
      <Stack gap="lg">
        <PageHeaderSkeleton />
        <AdminJoinRequestsCardSkeleton rows={3} />
      </Stack>
    );
  }
  if (isLoading) return null;

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('admin.requests.title')}
        subtitle={t('admin.requests.subtitle')}
      />
      {messageError !== null ? (
        <InlineStatus>{messageError}</InlineStatus>
      ) : null}
      {decisionError !== null ? (
        <InlineStatus>{decisionError}</InlineStatus>
      ) : null}
      <AdminJoinRequestsCard
        requests={requests}
        onApprove={handleApprove}
        onDecline={handleDecline}
        onMessage={messageMember}
        isPending={isPending}
      />
    </Stack>
  );
}
